import { TFunction } from 'i18next';
import { FieldItem } from '@lib/interfaces/form';
import Select, { SelectComponents } from '@lib/components/Select/Select';
import { GetLoadOptions } from '@lib/components/Select/types';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { getOptionsArrayValidation } from '@lib/utils/validationUtils';

const multiSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
} as unknown as SelectComponents;

export default function getOperationalScopeFields({
  t,
  equipmentLoadOptions,
  areasLoadOptions,
  formTemplatesLoadOptions,
  tasksLoadOptions,
  openEquipmentModal,
  openAreasModal,
  openFormTemplatesModal,
  openTasksModal,
  isFullAdmin = true,
  isPlanLocked,
  hasAssetsAccess = true,
  hasSitesAccess = true,
  hasTasksAccess = true,
}: {
  t: TFunction<'translation', undefined>;
  equipmentLoadOptions: GetLoadOptions;
  areasLoadOptions: GetLoadOptions;
  formTemplatesLoadOptions: GetLoadOptions;
  tasksLoadOptions: GetLoadOptions;
  openEquipmentModal?: () => void;
  openAreasModal?: () => void;
  openFormTemplatesModal?: () => void;
  openTasksModal?: () => void;
  isFullAdmin?: boolean;
  isPlanLocked?: boolean;
  hasAssetsAccess?: boolean;
  hasSitesAccess?: boolean;
  hasTasksAccess?: boolean;
}): FieldItem[] {
  // Equipment stays for both roles (free-plan single-asset rule still
  // makes it required when locked); only the truly-locked Areas / Form
  // Templates / Tasks are hidden for non-Full-Admin Users.
  const hideLockedFields = !!isPlanLocked && !isFullAdmin;
  const equipmentField: FieldItem = {
    name: DocumentFormFieldNames.Equipment,
    label: t('asset'),
    element: Select,
    isOptionalFn: () => !isPlanLocked,
    // Free-plan rule: a document must be linked to exactly one asset, so
    // tighten the validation to `min(1).max(1)` when EDM is plan-locked.
    validation: isPlanLocked
      ? getOptionsArrayValidation({ required: true, t }).max(
          1,
          t('free-plan-single-asset-error'),
        )
      : undefined,
    componentProps: openEquipmentModal
      ? {
          isAsync: true,
          isMulti: true,
          getLoadOptions: equipmentLoadOptions,
          defaultOptions: false,
          isClearable: true,
          isSearchable: false,
          openMenuOnClick: false,
          openMenuOnFocus: false,
          menuIsOpen: false,
          onSelectWrapperMouseDown: openEquipmentModal,
          components: multiSelectComponents,
        }
      : {
          isAsync: true,
          isMulti: true,
          getLoadOptions: equipmentLoadOptions,
          defaultOptions: false,
          isClearable: true,
          maxMenuHeight: 200,
          usePortal: true,
          components: multiSelectComponents,
        },
  };

  const areasField: FieldItem = {
    name: DocumentFormFieldNames.Areas,
    label: t('areas'),
    element: Select,
    isOptionalFn: () => true,
    componentProps: openAreasModal
      ? {
          isAsync: true,
          isMulti: true,
          getLoadOptions: areasLoadOptions,
          defaultOptions: false,
          isClearable: true,
          isSearchable: false,
          openMenuOnClick: false,
          openMenuOnFocus: false,
          menuIsOpen: false,
          onSelectWrapperMouseDown: isPlanLocked ? undefined : openAreasModal,
          components: multiSelectComponents,
          disabled: isPlanLocked,
          disabledTooltipProps: isPlanLocked
            ? { body: t('unlock-areas-tooltip') }
            : undefined,
        }
      : {
          isAsync: true,
          isMulti: true,
          getLoadOptions: areasLoadOptions,
          defaultOptions: false,
          isClearable: true,
          maxMenuHeight: 200,
          usePortal: true,
          components: multiSelectComponents,
          disabled: isPlanLocked,
          disabledTooltipProps: isPlanLocked
            ? { body: t('unlock-areas-tooltip') }
            : undefined,
        },
  };

  const formTemplatesField: FieldItem = {
    name: DocumentFormFieldNames.FormTemplates,
    label: t('form-templates'),
    element: Select,
    isOptionalFn: () => true,
    componentProps: openFormTemplatesModal
      ? {
          isAsync: true,
          isMulti: true,
          getLoadOptions: formTemplatesLoadOptions,
          defaultOptions: false,
          isClearable: true,
          isSearchable: false,
          openMenuOnClick: false,
          openMenuOnFocus: false,
          menuIsOpen: false,
          onSelectWrapperMouseDown: isPlanLocked
            ? undefined
            : openFormTemplatesModal,
          components: multiSelectComponents,
          disabled: isPlanLocked,
          disabledTooltipProps: isPlanLocked
            ? { body: t('unlock-form-templates-tooltip') }
            : undefined,
        }
      : {
          isAsync: true,
          isMulti: true,
          getLoadOptions: formTemplatesLoadOptions,
          defaultOptions: false,
          isClearable: true,
          maxMenuHeight: 200,
          usePortal: true,
          components: multiSelectComponents,
          disabled: isPlanLocked,
          disabledTooltipProps: isPlanLocked
            ? { body: t('unlock-form-templates-tooltip') }
            : undefined,
        },
  };

  const tasksField: FieldItem = {
    name: DocumentFormFieldNames.Tasks,
    label: t('tasks'),
    element: Select,
    isOptionalFn: () => true,
    componentProps: openTasksModal
      ? {
          isAsync: true,
          isMulti: true,
          getLoadOptions: tasksLoadOptions,
          defaultOptions: false,
          isClearable: true,
          isSearchable: false,
          openMenuOnClick: false,
          openMenuOnFocus: false,
          menuIsOpen: false,
          onSelectWrapperMouseDown: isPlanLocked ? undefined : openTasksModal,
          components: multiSelectComponents,
          disabled: isPlanLocked,
          disabledTooltipProps: isPlanLocked
            ? { body: t('unlock-tasks-tooltip') }
            : undefined,
        }
      : {
          isAsync: true,
          isMulti: true,
          getLoadOptions: tasksLoadOptions,
          defaultOptions: false,
          isClearable: true,
          maxMenuHeight: 200,
          usePortal: true,
          menuPlacement: 'top',
          components: multiSelectComponents,
          disabled: isPlanLocked,
          disabledTooltipProps: isPlanLocked
            ? { body: t('unlock-tasks-tooltip') }
            : undefined,
        },
  };

  const fields: FieldItem[] = [];
  if (hasAssetsAccess) fields.push(equipmentField);
  if (hasSitesAccess && !hideLockedFields) fields.push(areasField);
  if (hasTasksAccess && !hideLockedFields) {
    fields.push(formTemplatesField, tasksField);
  }
  if (fields.length === 0) return [];
  return [
    {
      name: DocumentFormFieldNames.OperationalScopeTitle,
      customRender: () => (
        <FieldTitleRender
          key="operationalScope"
          title={t('operational-scope')}
          tooltipText={t('operational-scope-desc')}
          isBlockStart
        />
      ),
    },
    ...fields,
  ];
}
